import { notify } from '@/lib/notify';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { captureViewportJpeg, waitForNextPaint } from '../lib/captureViewportScreenshot';
import { resolveBugReportEnvironment, submitPortalBugReport } from '../lib/portalBugReportBot';

export function useBugReport(): {
  startReport: () => Promise<void>;
  opened: boolean;
  capturing: boolean;
  screenshotBase64: string | null;
  submitting: boolean;
  onClose: () => void;
  onScreenshotChange: (jpegBase64: string) => void;
  onSubmit: (values: { summary: string; details: string; screenshotBase64: string }) => Promise<void>;
} {
  const medplum = useMedplum();
  const location = useLocation();
  const [opened, setOpened] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const captureIdRef = useRef(0);

  const reset = useCallback(() => {
    ++captureIdRef.current; // invalidate any in-flight capture
    setOpened(false);
    setCapturing(false);
    setScreenshotBase64(null);
    setSubmitting(false);
  }, []);

  const startReport = useCallback(async () => {
    const captureId = ++captureIdRef.current;
    setCapturing(true);
    await waitForNextPaint();
    try {
      const jpeg = await captureViewportJpeg();
      if (captureId !== captureIdRef.current) return;
      setScreenshotBase64(jpeg);
      setOpened(true);
    } catch {
      if (captureId !== captureIdRef.current) return;
      notify({ type: 'error', message: 'Could not capture the screen.' });
    } finally {
      if (captureId === captureIdRef.current) setCapturing(false);
    }
  }, []);

  const onSubmit = useCallback(
    async (values: { summary: string; details: string; screenshotBase64: string }) => {
      setSubmitting(true);
      try {
        const result = await submitPortalBugReport(medplum, {
          summary: values.summary,
          details: values.details,
          screenshotBase64: values.screenshotBase64,
          mimeType: 'image/jpeg',
          pagePath: location.pathname.slice(0, 200),
          environment: resolveBugReportEnvironment(),
        });
        if (!result.ok) {
          throw new Error('not ok');
        }
        notify({ type: 'success', message: 'Report submitted.' });
        reset();
      } catch {
        notify({ type: 'error', message: 'Could not submit the bug report.' });
        setSubmitting(false);
      }
    },
    [location.pathname, medplum, reset],
  );

  return {
    startReport,
    opened,
    capturing,
    screenshotBase64,
    submitting,
    onClose: () => {
      if (!submitting) reset();
    },
    onScreenshotChange: setScreenshotBase64,
    onSubmit,
  };
}
